import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { emailPressKit, getMatchReport, getPressKit, me, type MatchReportEvent } from '../api/portal';
import { ApiError } from '../api/client';
import { MatchupLabel } from '../components/TeamCrest';
import { copyText } from '../lib/clipboard';
import { useAuthStore } from '../stores/auth';
import { isPressLogin } from '../lib/portalRole';

async function downloadReport(id: string, format: 'csv' | 'html') {
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(`/client/v1/matches/${id}/report?format=${format}`, {
    headers: { Authorization: `Bearer ${token ?? ''}` },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  if (format === 'html') {
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    return;
  }
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `zapisnik-${id.slice(0, 8)}.csv`;
  a.click();
}

export function MatchReportPage() {
  const { id = '' } = useParams();
  const profile = useQuery({ queryKey: ['portal-me'], queryFn: me });
  const report = useQuery({ queryKey: ['portal-match-report', id], queryFn: () => getMatchReport(id), enabled: !!id });
  const r = report.data;
  const press = isPressLogin(profile.data);
  const [kitMsg, setKitMsg] = useState<string | null>(null);
  return (
    <div>
      <div className="page-head">
        <div className="page-kicker">Zapisnik</div>
        <h1 className="page-title">
          {r
            ? <MatchupLabel home={r.home} away={r.away} homeLogo={r.home_logo_url} awayLogo={r.away_logo_url} size={32} className="gap-2" />
            : 'Match report'}
        </h1>
        <p className="page-sub">
          <Link to="/matches" className="text-white underline-offset-2 hover:underline">← Matches</Link>
          {r?.matchweek && !press ? (
            <>
              {' · '}
              <Link to={`/rounds/${r.matchweek}`} className="text-white underline-offset-2 hover:underline">
                {r.round_name || `Kolo ${r.matchweek}`}
              </Link>
            </>
          ) : null}
        </p>
      </div>
      {r && (
        <div className="card mb-4 flex flex-wrap items-center gap-3 p-3 text-sm text-slate-300">
          <span className="text-xl font-semibold text-white">{r.score_home}–{r.score_away}</span>
          <span>{r.competition}</span>
          {r.official && (
            <span className="rounded-full bg-white/10 px-2 py-0.5 text-[11px] text-white">
              Zvanično{r.official_at ? ` · ${r.official_at.slice(11, 16)}` : ''}
            </span>
          )}
          <button type="button" className="btn-secondary text-xs" onClick={() => void downloadReport(id, 'csv')}>CSV</button>
          <button type="button" className="btn-secondary text-xs" onClick={() => void downloadReport(id, 'html')}>Print / PDF</button>
          <button
            type="button"
            className="btn-primary text-xs"
            onClick={() => getPressKit(id)
              .then((k) => copyText(k.url).then(() => setKitMsg(`Press kit: ${k.url}`)))
              .catch((e) => setKitMsg(e instanceof ApiError ? e.message : String(e)))}
          >
            Pošalji novinaru
          </button>
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={() => emailPressKit(id)
              .then((k) => setKitMsg(`Mejl: ${k.to}`))
              .catch((e) => setKitMsg(e instanceof ApiError ? e.message : String(e)))}
          >
            Mejl press kit
          </button>
          <Link to="/matches" className="btn-ghost text-xs">Shot-map widget on embed</Link>
        </div>
      )}
      {kitMsg && <div className="card mb-3 p-3 text-sm text-slate-200">{kitMsg}</div>}
      {report.isLoading && <div className="card p-6 text-sm text-slate-500">Loading…</div>}
      {r && (
        <div className="grid gap-4 md:grid-cols-3">
          <EventTable title="Strelci" rows={r.scorers} />
          <EventTable title="7m" rows={r.seven_meters} />
          <EventTable title="Isključenja" rows={r.exclusions} />
        </div>
      )}
    </div>
  );
}

function EventTable({ title, rows }: { title: string; rows: MatchReportEvent[] }) {
  return (
    <div className="card p-4">
      <div className="mb-2 text-[11px] uppercase tracking-wide text-slate-500">{title}</div>
      <table className="table">
        <tbody>
          {rows.map((e) => (
            <tr key={e.id}>
              <td className="w-12 text-slate-500">{e.minute != null ? `${e.minute}'` : '—'}</td>
              <td className="text-white">{e.player || e.event_type}</td>
              <td className="text-xs text-slate-400">{e.team}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td className="text-sm text-slate-500" colSpan={3}>—</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
